import { useState, useMemo } from 'react';
import { IconAction, RowActions } from '../../components/RowActions';
// Force Vite HMR reload
import PropTypes from 'prop-types';
import { Truck, Plus, Pen, Trash2, X as IconX, MapPin, IndianRupee } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  deliveryFeesOf,
  addDeliveryFee,
  updateDeliveryFee,
  deleteDeliveryFee,
  notify,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const EMPTY = { upto: '', fee: '' };

/* ─── Add / Edit Modal ─── */
const FeeModal = ({ open, mode, draft, setDraft, onClose, onSubmit, accent, showNotification }) => {
  const [errors, setErrors] = useState({});

  if (!open) {
    if (Object.keys(errors).length > 0) setErrors({});
    return null;
  }

  const handleLocalSubmit = () => {
    const newErrors = {};
    const upto = parseFloat(draft.upto);
    const fee = parseFloat(draft.fee);
    if (!draft.upto || isNaN(upto) || upto <= 0) newErrors.upto = 'Please fill the Up To (KM) right.';
    if (draft.fee === '' || isNaN(fee) || fee < 0) newErrors.fee = 'Please fill the Delivery Fee right.';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});
    onSubmit(draft);
  };
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(6px)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-[var(--theme-tertiary)] overflow-hidden"
        >
          {/* Header */}
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: accent }}>
                <Truck size={18} />
              </div>
              <div>
                <h3 className="text-base font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit Fee Slab' : 'New Fee Slab'}</h3>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Distance-based charge</p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close"
              className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50">
              <IconX size={16} />
            </button>
          </header>

          {/* Body */}
          <div className="px-6 py-5 flex flex-col gap-4">
            {/* Up To */}
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1">
                <MapPin size={11} /> Up To (KM) <span className="text-rose-500 ml-0.5">*</span>
              </span>
              <div className="relative">
                <input
                  type="number" min="0" step="0.1" placeholder="e.g. 5"
                  value={draft.upto}
                  onChange={e => {
                    setDraft(d => ({ ...d, upto: e.target.value }));
                    if (errors.upto) setErrors(errs => ({ ...errs, upto: undefined }));
                  }}
                  className={`w-full rounded-lg bg-gray-50 border ${errors.upto ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 pr-12 text-sm font-semibold text-[#0d0d0d]`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-bold text-slate-400">KM</span>
              </div>
              {errors.upto && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.upto}</p>}
            </label>

            {/* Fee */}
            <label className="block space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1">
                <IndianRupee size={11} /> Delivery Fee <span className="text-rose-500 ml-0.5">*</span>
              </span>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400">₹</span>
                <input
                  type="number" min="0" step="0.5" placeholder="e.g. 40"
                  value={draft.fee}
                  onChange={e => {
                    setDraft(d => ({ ...d, fee: e.target.value }));
                    if (errors.fee) setErrors(errs => ({ ...errs, fee: undefined }));
                  }}
                  className={`w-full rounded-lg bg-gray-50 border ${errors.fee ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 pl-7 text-sm font-semibold text-[#0d0d0d]`}
                />
              </div>
              {errors.fee && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.fee}</p>}
            </label>
          </div>

          {/* Footer */}
          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3"
            style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">
              Cancel
            </button>
            <button type="button" onClick={handleLocalSubmit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
              style={{ background: accent }}>
              {mode === 'edit' ? 'Save changes' : 'Add slab'}
            </button>
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
FeeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  accent: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
};

/* ─── Main Tab ─── */
export const DeliveryFeesTab = ({ currentOrg, accent, showNotification }) => {
  const { version: dataVersion } = useSharedStore();
  const [localVersion, setLocalVersion] = useState(0);
  const orgId = currentOrg?.id;
  const resolvedAccent = accent || currentOrg?.theme?.primary || '#0a5132';

  const fees = useMemo(() => {
    return deliveryFeesOf(orgId).slice().sort((a, b) => a.upto - b.upto);
  }, [orgId, localVersion, dataVersion]);

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [draft, setDraft] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const openCreate = () => { setDraft(EMPTY); setMode('create'); setModalOpen(true); };
  const openEdit = (f) => { setDraft({ upto: f.upto, fee: f.fee, _id: f.id }); setMode('edit'); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (validDraft) => {
    const upto = parseFloat(validDraft.upto);
    const fee = parseFloat(validDraft.fee);
    if (mode === 'edit') {
      updateDeliveryFee(validDraft._id, { upto, fee });
      notify();
      showNotification?.('Delivery fee updated', 'success');
    } else {
      await addDeliveryFee({ orgId, upto, fee });
      notify();
      showNotification?.('Delivery fee added', 'success');
    }
    setLocalVersion(v => v + 1);
    closeModal();
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    deleteDeliveryFee(confirmDelete.id);
    notify();
    showNotification?.('Delivery fee deleted', 'success');
    setConfirmDelete(null);
    setLocalVersion(v => v + 1);
  };

  return (
    <div className="flex flex-col gap-4">

      {/* ── Toolbar ── */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-sm font-extrabold text-[#0d0d0d]">Delivery Fees</p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Distance-based delivery charges (KM → ₹)</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg shrink-0"
          style={{ background: resolvedAccent, boxShadow: `0 12px 24px -12px ${resolvedAccent}` }}
        >
          <Plus size={14} /> New slab
        </button>
      </section>


      {/* ── Table / Empty ── */}
      {fees.length === 0 ? (
        <div className="py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
          <Truck size={28} className="mx-auto mb-3 text-slate-300" />
          <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No fee slabs yet.</p>
          <button type="button" onClick={openCreate}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]"
            style={{ background: resolvedAccent }}>
            <Plus size={12} /> Add first slab
          </button>
        </div>
      ) : (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden">
          {/* Table head */}
          <div className="grid px-5 py-3 border-b border-[var(--theme-tertiary)] bg-slate-50"
            style={{ gridTemplateColumns: '1fr 1fr 1fr 120px' }}>
            {['Up To (KM)', 'Delivery Fee', 'Coverage', 'Actions'].map((h, i) => (
              <span key={i} className={`text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 ${i === 3 ? 'text-center' : ''}`}>{h}</span>
            ))}
          </div>

          {/* Rows */}
          {fees.map((f, i) => {
            const prevUpto = i > 0 ? fees[i - 1].upto : 0;
            return (
              <motion.div
                key={f.id}
                layout
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid px-5 py-4 border-b border-[var(--theme-tertiary)] last:border-b-0 items-center hover:bg-slate-50 group transition-colors"
                style={{ gridTemplateColumns: '1fr 1fr 1fr 120px' }}
              >
                {/* KM badge */}
                <span className="text-[13px] font-extrabold text-[#0d0d0d]">
                  {f.upto} KM
                </span>

                {/* Fee */}
                <span className="text-[13px] font-extrabold text-[#0d0d0d]">₹{Number(f.fee).toFixed(2)}</span>

                {/* Coverage */}
                <span className="text-[12px] font-medium text-slate-500">
                  {prevUpto} – {f.upto} KM
                </span>

                {/* Actions */}
                <RowActions>
                  <IconAction icon={Pen} label={`Edit the fee up to ${f.upto} KM`} title="Edit" onClick={() => openEdit(f)} />
                  <IconAction icon={Trash2} tone="danger" label={`Remove the fee up to ${f.upto} KM`} title="Remove" onClick={() => setConfirmDelete(f)} />
                </RowActions>
              </motion.div>
            );
          })}

        </section>
      )}

      {/* ── Modals ── */}
      <FeeModal
        open={modalOpen}
        mode={mode}
        draft={draft}
        setDraft={setDraft}
        onClose={closeModal}
        onSubmit={handleSubmit}
        accent={resolvedAccent}
        showNotification={showNotification}
      />

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3"
                style={{ background: 'rgba(244,63,94,0.10)' }}>
                <Trash2 size={20} />
              </div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove this fee slab?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">
                Up to <strong>{confirmDelete.upto} KM → ₹{Number(confirmDelete.fee).toFixed(2)}</strong> will be permanently deleted.
              </p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)}
                className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">
                Cancel
              </button>
              <button type="button" onClick={doDelete}
                className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DeliveryFeesTab.propTypes = {
  currentOrg: PropTypes.object,
  accent: PropTypes.string,
  showNotification: PropTypes.func,
};

export default DeliveryFeesTab;
